import angular from 'angular'

import StrictObj from './strict-obj'
import debugFields from './debug-fields'

import { isPromise } from './bits'
import { wrap, assign, toArray, isMatch, isString, identity, constant, isFunction, isObjectLike } from 'lodash-es'

angular
  .module('ggs.utils')
  .factory('qh', qhFactory)

function qhFactory($log, $q, $timeout) {
  const CANCELED_STR = 'canceled'
  const CANCELED_EXT = { isCanceled: true }

  return StrictObj({
    catchCanceled,
    markCanceled,
    isCanceled,
    makeAttempt,
    makeIfElse,
    makeUnless,
    makeDelay,
    makeCatch,
    makeFork,
    makeOver,
    makeRace,
    makeJoin,
    makeFlow,
    makeAnd,
    makeXor,
    makeOr,
    makeIf,
    makeEq,
    tapCatch,
    debounce,
    throttle,
    attempt,
    eqWith,
    ifElse,
    unless,
    delay,
    catch: qhCatch,
    fork,
    wrap: qhWrap,
    over: qhOver,
    race,
    join,
    flow: qhFlow,
    and,
    xor,
    try: qhTry,
    tap,
    if: qhIf,
    or,
    eq: qhEq
  })

  // delay

  function delay(ms, value) {
    return makeDelay(ms)(value)
  }

  function makeDelay(ms) {
    return function makeDelay_wrapper(value) {
      const promise = $timeout(() => value, ms)
      promise.cancel = () => $timeout.cancel(promise)
      return promise
    }
  }

  // catchCanceled

  function catchCanceled(canceledErrCallback) {
    return debugFields(allErrCallback, {
      _onCanceledErrAction: canceledErrCallback
    })

    function allErrCallback(err) {
      return isCanceled(err)
        ? canceledErrCallback(err)
        : $q.reject(err)
    }
  }

  function markCanceled(err) {
    return isString(err)
      ? CANCELED_STR
      : assign(err, CANCELED_EXT)
  }

  function isCanceled(err) {
    const canceledStr = isString(err) && err === CANCELED_STR
    const canceledExt = isObjectLike(err) && isMatch(err, CANCELED_EXT)
    return canceledStr || canceledExt
  }

  // try

  function qhTry(action, args) {
    try {
      const res = action.apply(null, args)
      return isPromise(res)
        ? res
        : $q.resolve(res)
    } catch (err) {
      $log.warn('qh#try: action throw error:', err)
      return $q.reject(err)
    }
  }

  // attempt

  function attempt(action, args) {
    try {
      const res = action.apply(null, args)
      return isPromise(res)
        ? res.catch(tapCatch(wrap('qh#attempt: action throw error:', $log.warn))).catch(identity)
        : $q.resolve(res)
    } catch (err) {
      $log.warn('qh#attempt: action throw error:', err)
      return $q.resolve(err)
    }
  }

  function makeAttempt(action) {
    return debugFields(makeAttempt_wrapper, {
      _action: action
    })

    function makeAttempt_wrapper() {
      try {
        const res = action.apply(null, arguments)
        return isPromise(res)
          ? res.catch(tapCatch(wrap('qh#makeAttempt: action throw error:', $log.warn))).catch(identity)
          : $q.resolve(res)
      } catch (err) {
        $log.warn('qh#makeAttempt: action throw error:', err)
        return $q.resolve(err)
      }
    }
  }

  // if

  function makeIf(first, action) {
    return debugFields(makeIf_wrapper, {
      _first: first,
      _action: action
    })

    function makeIf_wrapper() {
      return qhIf(first, action, arguments)
    }
  }

  function qhIf(first, action, args) {
    if (isFunction(first)) {
      try {
        const condition = first.apply(null, args)
        return qhIf(condition, action, args)
      } catch (err) {
        $log.warn('qh#if: condition function throw error:', err)
        return $q.reject(err)
      }
    } else if (isPromise(first)) {
      return first.then((condition) => qhIf(condition, action, args))
    } else {
      return !!first
        ? qhTry(action, args)
        : $q.resolve()
    }
  }

  // ifElse

  function makeIfElse(first, action, alt) {
    return debugFields(makeIfElse_wrapper, {
      _first: first,
      _action: action,
      _alt: alt
    })

    function makeIfElse_wrapper() {
      return ifElse(first, action, alt, arguments)
    }
  }

  function ifElse(first, action, alt, args) {
    if (isFunction(first)) {
      try {
        const condition = first.apply(null, args)
        return ifElse(condition, action, alt, args)
      } catch (err) {
        $log.warn('qh#ifElse: condition function throw error:', err)
        return $q.reject(err)
      }
    } else if (isPromise(first)) {
      return first.then((condition) => ifElse(condition, action, alt, args))
    } else if (!!first) {
      return qhTry(action, args)
    } else if (isFunction(alt)) {
      return qhTry(alt, args)
    } else {
      return $q.resolve(alt)
    }
  }

  // unless

  function makeUnless(first, action) {
    return debugFields(makeUnless_wrapper, {
      _first: first,
      _action: action
    })

    function makeUnless_wrapper() {
      return unless(first, action, arguments)
    }
  }

  function unless(first, action, args) {
    if (isFunction(first)) {
      try {
        const condition = first.apply(null, args)
        return unless(condition, action, args)
      } catch (err) {
        $log.warn('qh#unless: condition function throw error:', err)
        return $q.reject(err)
      }
    } else if (isPromise(first)) {
      return first.then((condition) => unless(condition, action, args))
    } else {
      return !!first
        ? $q.resolve()
        : qhTry(action, args)
    }
  }

  // catch

  function makeCatch(action, catchFunc) {
    return debugFields(makeCatch_wrapper, {
      _action: action,
      _catchFunc: catchFunc
    })

    function makeCatch_wrapper() {
      return qhCatch(action, catchFunc, arguments)
    }
  }

  function qhCatch(action, catchFunc, args) {
    return qhTry(action, args).catch(catchFunc)
  }

  // fork

  function makeFork(action, thenFunc, catchFunc) {
    return debugFields(makeFork_wrapper, {
      _action: action,
      _thenFunc: thenFunc,
      _catchFunc: catchFunc
    })

    function makeFork_wrapper() {
      return fork(action, thenFunc, catchFunc, arguments)
    }
  }

  function fork(action, thenFunc, catchFunc, args) {
    return qhTry(action, args).then(thenFunc, catchFunc)
  }

  // wrap

  function qhWrap(action) {
    return debugFields(wrap_wrapper, {
      _action: action
    })

    function wrap_wrapper() {
      try {
        const res = action.apply(null, arguments)
        return isPromise(res)
          ? res
          : $q.resolve(res)
      } catch (err) {
        $log.warn('qh#wrap: action throw error:', err)
        return $q.reject(err)
      }
    }
  }

  // race

  function race(...promises) {
    return $q.race(promises)
  }

  function makeRace(...actions) {
    return debugFields(makeRace_wrapper, {
      _actions: actions
    })

    function makeRace_wrapper() {
      try {
        const promises = []
        for (const action of actions) {
          const prms = action.apply(null, arguments)
          promises.push(prms)
        }
        return $q.race(promises)
      } catch (err) {
        $log.warn('qh#makeRace: action throw error:', err)
        return $q.reject(err)
      }
    }
  }

  // join

  function join(...promises) {
    return $q.all(promises)
  }

  function makeJoin(...actions) {
    return debugFields(makeJoin_wrapper, {
      _actions: actions
    })

    function makeJoin_wrapper() {
      try {
        const promises = []
        for (const action of actions) {
          const prms = action.apply(null, arguments)
          promises.push(prms)
        }
        return $q.all(promises)
      } catch (err) {
        $log.warn('qh#makeJoin: action throw error:', err)
        return $q.reject(err)
      }
    }
  }

  // tap

  function tap(action) {
    return debugFields(tap_wrapper, {
      _action: action
    })

    function tap_wrapper(res) {
      return qhTry(action, [res]).then(() => res)
    }
  }

  function tapCatch(action) {
    return debugFields(tapCatch_wrapper, {
      _action: action
    })

    function tapCatch_wrapper(err) {
      return qhTry(action, [err]).then(() => $q.reject(err))
    }
  }

  // over

  function qhOver(...actions) {
    return iterateOver(actions)
  }

  function makeOver(...actions) {
    return debugFields(makeOver_wrapper, {
      _actions: actions
    })

    function makeOver_wrapper() {
      return iterateOver(actions, arguments)
    }
  }

  function iterateOver(actions, args = undefined, index = 0, results = []) {
    try {
      const isLast = index === (actions.length - 1)
      const action = actions[index]
      const result = action.apply(null, args)
      results.push(result)
      if (isLast) {
        return $q.all(results)
      } else {
        return isPromise(result)
          ? result.then(() => iterateOver(actions, args, index + 1, results))
          : iterateOver(actions, args, index + 1, results)
      }
    } catch (err) {
      $log.warn(`qh#iterateOver: action with [index=${index}] throw error:`, err)
      return $q.reject(err)
    }
  }

  // flow

  function qhFlow(...actions) {
    return iterateFlow(actions)
  }

  function makeFlow(...actions) {
    return debugFields(makeFlow_wrapper, {
      _actions: actions
    })

    function makeFlow_wrapper() {
      return iterateFlow(actions, arguments)
    }
  }

  function iterateFlow(actions, args = undefined, index = 0) {
    try {
      const isLast = index === (actions.length - 1)
      const action = actions[index]
      const result = action.apply(null, args)
      if (isLast) {
        return isPromise(result)
          ? result
          : $q.resolve(result)
      } else {
        return isPromise(result)
          ? result.then((rawResult) => iterateFlow(actions, [rawResult], index + 1))
          : iterateFlow(actions, [result], index + 1)
      }
    } catch (err) {
      $log.warn(`qh#iterateFlow: action with [index=${index}] throw error:`, err)
      return $q.reject(err)
    }
  }

  // and

  function and(...actions) {
    return iterateAnd(actions)
  }

  function makeAnd(...actions) {
    return debugFields(makeAnd_wrapper, {
      _actions: actions
    })

    function makeAnd_wrapper() {
      return iterateAnd(actions, arguments)
    }
  }

  function iterateAnd(actions, args = undefined) {
    let resolved = 0
    const promises = []

    for (let index = 0, length = actions.length; index !== length; index++) {
      try {
        const action = actions[index]
        const result = action.apply(null, args)
        if (isPromise(result)) {
          promises.push(result)
        } else if (!result) {
          return $q.resolve(false)
        }
      } catch (err) {
        $log.warn(`qh#iterateAnd: action with [index=${index}] throw error:`, err)
        return $q.reject(err)
      }
    }

    if (promises.length === 0) {
      return $q.resolve(true)
    }

    const deferred = $q.defer()
    const proxyResolve = (value) => {
      resolved++
      if (!value) {
        return deferred.resolve(false)
      }
      if (promises.length === resolved) {
        return deferred.resolve(true)
      }
    }

    for (const promise of promises) {
      promise.then(proxyResolve, deferred.reject)
    }

    return deferred.promise
  }

  // or

  function or(...actions) {
    return iterateOr(actions)
  }

  function makeOr(...actions) {
    return debugFields(makeAnd_wrapper, {
      _actions: actions
    })

    function makeAnd_wrapper() {
      return iterateOr(actions, arguments)
    }
  }

  function iterateOr(actions, args = undefined) {
    let resolved = 0
    const promises = []

    for (let index = 0, length = actions.length; index !== length; index++) {
      try {
        const action = actions[index]
        const result = action.apply(null, args)
        if (isPromise(result)) {
          promises.push(result)
        } else if (!!result) {
          return $q.resolve(true)
        }
      } catch (err) {
        $log.warn(`qh#iterateOr: action with [index=${index}] throw error:`, err)
        return $q.reject(err)
      }
    }

    if (promises.length === 0) {
      return $q.resolve(false)
    }

    const deferred = $q.defer()
    const proxyResolve = (value) => {
      resolved++
      if (!!value) {
        return deferred.resolve(true)
      }
      if (promises.length === resolved) {
        return deferred.resolve(false)
      }
    }

    for (const promise of promises) {
      promise.then(proxyResolve, deferred.reject)
    }

    return deferred.promise
  }

  // xor

  function xor(...actions) {
    return iterateXor(actions)
  }

  function makeXor(...actions) {
    return debugFields(makeXor_wrapper, {
      _actions: actions
    })

    function makeXor_wrapper() {
      return iterateXor(actions, arguments)
    }
  }

  function iterateXor(actions, args = undefined) {
    let count = 0
    let resolved = 0
    const promises = []

    for (let index = 0, length = actions.length; index !== length; index++) {
      try {
        const action = actions[index]
        const result = action.apply(null, args)
        if (isPromise(result)) {
          promises.push(result)
        } else if (result) {
          if (count === 0) {
            count++
          } else {
            count++
            break
          }
        }
      } catch (err) {
        $log.warn(`qh#iterateXor: action with [index=${index}] throw error:`, err)
        return $q.reject(err)
      }
    }

    if (count === 2) {
      return $q.resolve(false)
    }

    if (promises.length === 0) {
      return $q.resolve(count === 1)
    }

    const deferred = $q.defer()
    const proxyResolve = (value) => {
      resolved++
      if (value) {
        count++
      }
      if (count === 2) {
        return deferred.resolve(false)
      }
      if (promises.length === resolved) {
        return deferred.resolve(count === 1)
      }
    }

    for (const promise of promises) {
      promise.then(proxyResolve, deferred.reject)
    }

    return deferred.promise
  }

  // eq

  function qhEq(...actions) {
    return iterateEq(actions)
  }

  function makeEq(...actions) {
    return debugFields(makeEq_wrapper, {
      _actions: actions
    })

    function makeEq_wrapper() {
      return iterateEq(actions, arguments)
    }
  }

  function eqWith(...actions) {
    return debugFields(makeEqWith_wrapper, {
      _actions: actions
    })

    function makeEqWith_wrapper() {
      const args = toArray(arguments)
      const extra = args.map(constant)
      return iterateEq([...actions, ...extra])
    }
  }

  function iterateEq(actions, args = undefined) {
    let first = undefined
    let firstInitialized = false

    let resolved = 0
    const promises = []

    for (let index = 0, length = actions.length; index !== length; index++) {
      try {
        const action = actions[index]
        const result = action.apply(null, args)
        if (isPromise(result)) {
          promises.push(result)
        } else if (!firstInitialized) {
          first = result
          firstInitialized = true
        } else if (first !== result) {
          return $q.resolve(false)
        }
      } catch (err) {
        $log.warn(`qh#iterateEq: action with [index=${index}] throw error:`, err)
        return $q.reject(err)
      }
    }

    if (promises.length === 0) {
      return $q.resolve(true)
    }

    const deferred = $q.defer()
    const proxyResolve = (value) => {
      resolved++
      if (!firstInitialized) {
        first = value
        firstInitialized = true
      } else if (first !== value) {
        return deferred.resolve(false)
      }

      if (promises.length === resolved) {
        return deferred.resolve(true)
      }
    }

    for (const promise of promises) {
      promise.then(proxyResolve, deferred.reject)
    }

    return deferred.promise
  }

  // debounce

  function debounce(action, delay) {
    let args = null
    let deferred = null
    let timeoutId = null

    return debugFields(debounce_wrapper, {
      _delay: delay,
      _action: action
    })

    function debounce_wrapper() {
      args = arguments
      $timeout.cancel(timeoutId)
      timeoutId = $timeout(callback, delay)
      if (!deferred) deferred = $q.defer()
      return deferred.promise
    }

    function callback() {
      const local = deferred
      deferred = null
      qhTry(action, args).then(local.resolve, local.reject)
    }
  }

  // throttle

  function throttle(action, delay) {
    let args = null
    let lastCall = Date.now() - delay - 1
    let deferred = null

    return debugFields(throttle_wrapper, {
      _delay: delay,
      _action: action
    })

    function throttle_wrapper() {
      args = arguments

      if (deferred) {
        return deferred.promise
      }

      const diff = (lastCall + delay) - Date.now()
      if (diff <= 0) {
        return invoke()
      }

      deferred = $q.defer()
      $timeout(callback, diff)
      return deferred.promise
    }

    function invoke() {
      lastCall = Date.now()
      return qhTry(action, args)
    }

    function callback() {
      const local = deferred
      deferred = null
      invoke().then(local.resolve, local.reject)
    }
  }
}
